import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import {alpha} from "@mui/material/styles";
import * as React from "react";
import {directionColor, signed} from "./insightsFormat";

const TABLE_MAX_HEIGHT = 538;

export type InsightsColumnKind =
    | "label"
    | "text"
    | "value"
    | "verdict"
    | "compare"
    | "detail"
    | "delta";

export interface InsightsColumn<Row> {
    key: string;
    label: string;
    kind: InsightsColumnKind;
    width?: number;
    cell: (row: Row) => React.ReactNode;
    format?: (value: number) => string;
    color?: (row: Row) => string | undefined;
    lowerIsBetter?: boolean | ((row: Row) => boolean);
    comparison?: boolean;
}

interface Props<Row> {
    label: string;
    columns: ReadonlyArray<InsightsColumn<Row> | false | null | undefined>;
    rows: ReadonlyArray<Row>;
    rowKey: (row: Row) => React.Key;
}

const NUMBER_WIDTH = 112;
const NUMBER_MIN_WIDTH = 84;
const TEXT_WIDTH = 124;
const LABEL_MIN_WIDTH = 124;

const CELL_LINE_HEIGHT = "20px";
const CELL_PADDING_Y = "10px";
const CELL_PADDING_X = "10px";

const isNumeric = (kind: InsightsColumnKind) => kind !== "label" && kind !== "text";

const COMPARE_CLASS = "insights-compare";

const bandClass = <Row, >(column: InsightsColumn<Row>) =>
    column.kind === "compare" || column.comparison ? COMPARE_CLASS : undefined;

const KIND_SX: Record<InsightsColumnKind, Record<string, unknown>> = {
    label: {overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"},
    text: {color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"},
    value: {fontWeight: 600},
    verdict: {fontWeight: 700},
    compare: {color: "text.disabled"},
    detail: {color: "text.secondary"},
    delta: {fontWeight: 700}
};

function resolveCell<Row>(column: InsightsColumn<Row>, row: Row): { content: React.ReactNode; color?: string } {
    const raw = column.cell(row);

    if (!isNumeric(column.kind) || typeof raw !== "number") {
        return raw == null && isNumeric(column.kind)
            ? {content: "—", color: "text.disabled"}
            : {content: raw, color: column.color?.(row)};
    }

    const format = column.format ?? String;

    if (column.kind === "delta") {
        const {text, direction} = signed(raw, format);

        const lowerIsBetter =
            typeof column.lowerIsBetter === "function" ? column.lowerIsBetter(row) : !!column.lowerIsBetter;

        return {content: text, color: column.color?.(row) ?? directionColor(direction, lowerIsBetter)};
    }

    return {content: format(raw), color: column.color?.(row)};
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function columnWidths<Row>(columns: InsightsColumn<Row>[], available: number | null): Map<string, number | undefined> {
    const widthOf = (column: InsightsColumn<Row>): number | undefined => {
        if (column.kind === "label") return undefined;
        if (column.kind === "text") return column.width ?? TEXT_WIDTH;
        return column.width;
    };

    const flexible = columns.filter(column => isNumeric(column.kind) && column.width == null);
    const fixedSum = columns.reduce((sum, column) => sum + (widthOf(column) ?? 0), 0);

    const number =
        available == null || flexible.length === 0
            ? NUMBER_WIDTH
            : clamp(Math.floor((available - fixedSum - LABEL_MIN_WIDTH) / flexible.length), NUMBER_MIN_WIDTH, NUMBER_WIDTH);

    const label = available == null ? undefined : Math.max(LABEL_MIN_WIDTH, available - fixedSum - number * flexible.length);

    return new Map(
        columns.map(column => [
            column.key,
            column.kind === "label" ? label : isNumeric(column.kind) && column.width == null ? number : widthOf(column)
        ])
    );
}

export function InsightsTable<Row>({label, columns, rows, rowKey}: Props<Row>) {
    const visible = columns.filter((column): column is InsightsColumn<Row> => !!column);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [available, setAvailable] = React.useState<number | null>(null);

    React.useLayoutEffect(() => {
        const element = containerRef.current;
        if (element == null) return;

        const observer = new ResizeObserver(entries => setAvailable(Math.floor(entries[0].contentRect.width)));
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    const widths = columnWidths(visible, available);

    return (
        <TableContainer
            ref={containerRef}
            sx={theme => ({
                maxHeight: TABLE_MAX_HEIGHT,
                scrollbarGutter: "stable",
                scrollbarWidth: "thin",
                scrollbarColor: `${alpha(theme.palette.text.primary, 0.25)} transparent`,

                "&::-webkit-scrollbar": {width: 8, height: 8},
                "&::-webkit-scrollbar-track": {backgroundColor: "transparent"},
                "&::-webkit-scrollbar-thumb": {
                    borderRadius: 4,
                    backgroundColor: alpha(theme.palette.text.primary, 0.22)
                },
                "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: alpha(theme.palette.text.primary, 0.35)
                },

                "& .MuiTableCell-root": {
                    fontSize: 14,
                    lineHeight: CELL_LINE_HEIGHT,
                    px: CELL_PADDING_X,
                    py: CELL_PADDING_Y
                },
                "& thead .MuiTableCell-root": {
                    fontWeight: 600,
                    color: "text.secondary",
                    bgcolor: "background.paper",
                    whiteSpace: "normal",
                    verticalAlign: "bottom"
                },
                "& tbody .MuiTableCell-alignRight": {
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums"
                },
                "& thead th:first-of-type, & tbody th": {
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    backgroundColor: theme.palette.background.paper
                },
                "& thead th:first-of-type": {zIndex: 3},
                "& tbody tr:hover, & tbody tr:hover th": {backgroundColor: theme.palette.grey[50]},
                [`& .MuiTableCell-root.${COMPARE_CLASS}`]: {backgroundColor: theme.palette.grey[100]},
                [`& tbody tr:hover .MuiTableCell-root.${COMPARE_CLASS}`]: {backgroundColor: theme.palette.grey[200]}
            })}
        >
            <Table stickyHeader aria-label={label} sx={{tableLayout: "fixed"}}>
                <TableHead>
                    <TableRow>
                        {visible.map(column => (
                            <TableCell
                                key={column.key}
                                align={isNumeric(column.kind) ? "right" : "left"}
                                className={bandClass(column)}
                                sx={{width: widths.get(column.key)}}
                            >
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map(row => (
                        <TableRow key={rowKey(row)}>
                            {visible.map(column => {
                                const {content, color} = resolveCell(column, row);

                                if (column.kind === "label") {
                                    return (
                                        <TableCell
                                            key={column.key}
                                            component={"th"}
                                            scope={"row"}
                                            title={typeof content === "string" ? content : undefined}
                                            sx={{...KIND_SX.label, ...(color && {color})}}
                                        >
                                            {content}
                                        </TableCell>
                                    );
                                }

                                return (
                                    <TableCell
                                        key={column.key}
                                        align={isNumeric(column.kind) ? "right" : "left"}
                                        className={bandClass(column)}
                                        sx={{...KIND_SX[column.kind], ...(color && {color})}}
                                    >
                                        {content}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
